import {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  SharedFeatureService,
} from '@feature-hub/core'
import { AsyncSsrManagerV1 } from '@feature-hub/async-ssr-manager'
import createStore, { RootState } from './createStore'
import { VERSIONS, ID, DEFAULT_FRONTEND_ENVIRONMENT } from './constants'
import { api as gcsApi } from './api/gcs/generated'
import { crsApi } from './api/crs/react'
import { SerializedStateManagerV1, serializedStateManagerDefinition } from '@feature-hub/serialized-state-manager'
import { hydrate } from './actions'
import { selectClientConfigurationQueryResult } from './api/crs/selectors'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { FEATURE_SERVICE_VERSION, FEATURE_SERVICE_ID } from '@client-hub/shared/constants/feature-hub'
import { updateDomain } from './slices/metadata'

export type StoreFeatureService = ReturnType<typeof createStore>
export interface SharedStoreFeatureService extends SharedFeatureService {
  readonly [VERSIONS.PRE_RELEASE]: FeatureServiceBinder<StoreFeatureService>
}
interface Dependencies extends FeatureServices {
  readonly 's2:async-ssr-manager': AsyncSsrManagerV1 | undefined
  readonly 's2:serialized-state-manager': SerializedStateManagerV1
}
type StoreFeatureServiceConfigOptions = {
  domain: string
}

export const defineStoreFeatureService = ({
  domain,
}: StoreFeatureServiceConfigOptions): FeatureServiceProviderDefinition<SharedStoreFeatureService, Dependencies> => ({
  id: ID,
  dependencies: {
    featureServices: { [serializedStateManagerDefinition.id]: FEATURE_SERVICE_VERSION.SERIALIZED_STATE_MANAGER },
  },
  optionalDependencies: {
    featureServices: {
      [FEATURE_SERVICE_ID.ASYNC_SSR_MANAGER]: FEATURE_SERVICE_VERSION.ASYNC_SSR_MANAGER,
    },
  },
  create: ({ featureServices }) => {
    let rootState: RootState
    const { store, persistor } = createStore()
    // Update `domain` so below SSR related queries can with appropriate headers
    store.dispatch(updateDomain(domain))
    const asyncSsrManager = featureServices['s2:async-ssr-manager']
    const serializedStateManager = featureServices['s2:serialized-state-manager']
    const queryForSsrData = async () => {
      store.dispatch(crsApi.endpoints.clientConfiguration.initiate())
      await store.dispatch(crsApi.util.getRunningQueryThunk('clientConfiguration', undefined))
      const { data } = selectClientConfigurationQueryResult(store.getState())
      const name = data?.frontend_environment || DEFAULT_FRONTEND_ENVIRONMENT
      store.dispatch(gcsApi.endpoints.GetThemeAndBrandingAssets.initiate({ name }))
      await store.dispatch(gcsApi.util.getRunningQueryThunk('GetThemeAndBrandingAssets', { name }))
      rootState = store.getState()
    }

    if (asyncSsrManager) {
      // Server Side Branch
      serializedStateManager.register(() => JSON.stringify(rootState))
      asyncSsrManager.scheduleRerender(queryForSsrData())
    } else if (process.env.NODE_ENV === 'production') {
      // Client Side Branch
      store.dispatch(hydrate(JSON.parse(serializedStateManager.getSerializedState()!)))
    }

    return {
      [VERSIONS.PRE_RELEASE]: () => ({
        featureService: {
          store,
          persistor,
        },
      }),
    }
  },
})
